/**
 * MoodMapCalendar - Monthly calendar grid view
 */

import { useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isSameMonth,
  isToday,
  isFuture,
} from 'date-fns';
import { tr } from 'date-fns/locale';
import { useMoodMapStore, MOODS } from '../../stores/moodmapStore';

const WEEKDAYS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

const MoodMapCalendar = () => {
  const { currentMonth, entries, selectDate } = useMoodMapStore();

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentMonth]);

  // Create a map of entries by date
  const entriesByDate = useMemo(() => {
    const map = {};
    entries.forEach((entry) => {
      map[entry.date] = entry;
    });
    return map;
  }, [entries]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-gray-900/50 border border-gray-800 rounded-xl overflow-hidden"
    >
      {/* Weekday headers */}
      <div className="grid grid-cols-7 bg-gray-800/50">
        {WEEKDAYS.map((day) => (
          <div
            key={day}
            className="py-3 text-center text-sm font-medium text-gray-400"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {calendarDays.map((day, index) => {
          const dateStr = format(day, 'yyyy-MM-dd');
          const entry = entriesByDate[dateStr];
          const isCurrentMonth = isSameMonth(day, currentMonth);
          const isTodayDate = isToday(day);
          const isFutureDate = isFuture(day);

          return (
            <CalendarDay
              key={dateStr}
              day={day}
              entry={entry}
              isCurrentMonth={isCurrentMonth}
              isToday={isTodayDate}
              isFuture={isFutureDate}
              onClick={() => !isFutureDate && selectDate(dateStr)}
            />
          );
        })}
      </div>
    </motion.div>
  );
};

const CalendarDay = ({ day, entry, isCurrentMonth, isToday, isFuture, onClick }) => {
  const dayNumber = format(day, 'd');
  const mood = entry?.mood;
  const itemCount = entry?.items?.length || 0;

  // Get mood color
  const getMoodBg = () => {
    if (!mood) return '';
    const colors = {
      great: 'bg-emerald-500/20 border-emerald-500/30',
      good: 'bg-green-500/20 border-green-500/30',
      neutral: 'bg-yellow-500/20 border-yellow-500/30',
      bad: 'bg-orange-500/20 border-orange-500/30',
      awful: 'bg-red-500/20 border-red-500/30',
    };
    return colors[mood] || '';
  };

  return (
    <motion.button
      whileHover={{ scale: isFuture ? 1 : 1.02 }}
      whileTap={{ scale: isFuture ? 1 : 0.98 }}
      onClick={onClick}
      disabled={isFuture}
      className={`
        relative min-h-[100px] p-2 border-r border-b border-gray-800
        transition-colors text-left
        ${!isCurrentMonth ? 'opacity-30' : ''}
        ${isFuture ? 'cursor-not-allowed opacity-50' : 'hover:bg-gray-800/50 cursor-pointer'}
        ${isToday ? 'ring-2 ring-purple-500 ring-inset' : ''}
        ${mood ? getMoodBg() : ''}
      `}
    >
      {/* Day number */}
      <div
        className={`
          text-sm font-medium mb-1
          ${isToday ? 'text-purple-400' : 'text-gray-300'}
        `}
      >
        {dayNumber}
      </div>

      {/* Mood emoji */}
      {mood && (
        <div className="text-2xl mb-1">{MOODS[mood]?.emoji}</div>
      )}

      {/* Item count */}
      {itemCount > 0 && (
        <div className="text-xs text-gray-400">
          {itemCount} not
        </div>
      )}

      {/* Entry indicator (if has entry but no mood) */}
      {entry && !mood && (
        <div className="absolute top-2 right-2 w-2 h-2 bg-purple-500 rounded-full" />
      )}
    </motion.button>
  );
};

export default MoodMapCalendar;
